import _ from 'lodash';
import cn from 'classnames';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { itemPropTypes } from 'shared/proptypes/AggregationItem';
import { Submit } from 'shared/components/Form';
import Card, { CardList } from 'shared/components/Card';
import Loadable from 'shared/components/Loadable';
import TitleWithButton from 'shared/components/TitleWithButton';
import Icon from 'shared/components/Icon/Icon';
import apiClient from 'userportal/lib/ApiClient';
import JobPreview from 'userportal/components/Job/Preview';
import { ALL_JOBS_LINK, DEFAULT_CURRENCY } from 'shared/constants';
import { userportal as routes, createBackofficeLink, backoffice, createRoute, createStaticsLink } from 'shared/routes';
import googleAnalytics from 'userportal/lib/googleAnalytics';
import styles from './Intro.module.scss';

const HomeIntro = ({
  loggedIn,
  currency,
  withSeoLinks,
  searchItems,
  onFieldClick,
  onRoleClick,
  onSkillClick,
  onEmploymentTypeClick,
  onCompanySizeClick,
  onCountryClick,
  onCityClick,
  onBenefitClick,
  onRateClick,
  onSuitableForClick,
}) => {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, jobs: [], companies: [] });
  const { t, i18n } = useTranslation('userportal');

  const categories = useMemo(() => [
    {
      id: 'junior',
      icon: 'la la-graduation-cap',
      la: true,
      route: '/suitableFor/juniors',
      cb: onSuitableForClick({ slug: 'juniors', title: t('common:suitableFor.juniors') })
    },
    {
      id: 'senior',
      icon: 'la la-user-tie',
      la: true,
      route: '/suitableFor/seniors',
      cb: onSuitableForClick({ slug: 'seniors', title: t('common:suitableFor.seniors') })
    },
    {
      id: 'frontend',
      icon: 'la la-laptop-code',
      la: true,
      route: '/role/frontend-developer',
      cb: onRoleClick({ slug: 'frontend-developer' })
    },
    {
      id: 'dot-net',
      icon: 'dot-net',
      la: false,
      route: '/skill/dot-net',
      cb: onSkillClick({ slug: 'dot-net', title: '.NET' })
    },
    {
      id: 'java',
      icon: 'java',
      la: false,
      route: '/skill/java',
      cb: onSkillClick({ slug: 'java', title: 'Java' })
    },
    {
      id: 'python',
      icon: 'python',
      la: false,
      route: '/skill/python',
      cb: onSkillClick({ slug: 'python', title: 'Python' })
    },
    {
      id: 'qa',
      icon: 'la la-user-check',
      la: true,
      route: '/field/qa-testing',
      cb: onFieldClick({ slug: 'qa-testing' })
    },
    {
      id: 'ops',
      icon: 'la la-user-shield',
      la: true,
      route: '/field/it-operations-infrastructure',
      cb: onFieldClick({ slug: 'it-operations-infrastructure' })
    },
    {
      id: 'design-engineer',
      icon: 'la la-user-cog',
      la: true,
      route: '/role/design-engineer',
      cb: onRoleClick({ slug: 'design-engineer' })
    },
    {
      id: 'quality-inspector',
      icon: 'la la-user-check',
      la: true,
      route: '/role/quality-inspector',
      cb: onRoleClick({ slug: 'quality-inspector' })
    },
    // {
    //   id: 'product',
    //   icon: 'la la-user-tie',
    //   la: true,
    //   route: '/role/product-manager',
    //   cb: onRoleClick({ slug: 'product-manager' })
    // },
    {
      id: 'it-analyst',
      icon: 'la la-user-tie',
      la: true,
      route: '/role/it-analyst',
      cb: onRoleClick({ slug: 'it-analyst' })
    },
    {
      id: 'project',
      icon: 'la la-user-tie',
      la: true,
      route: '/role/project-manager',
      cb: onRoleClick({ slug: 'project-manager' })
    },
    // {
    //   id: 'dwn',
    //   icon: 'la la-table',
    //   la: true,
    //   route: '/role/data-bi-dwh-bigdata',
    //   cb: onRoleClick({ slug: 'data-bi-dwh-bigdata' })
    // },
  ], [onRoleClick, onFieldClick, onSkillClick, onSuitableForClick]);

  const loadData = async () => {
    setState({ loading: true, jobs: [], companies: [] });
    try {
      const [jobs, companies] = await Promise.all([
        apiClient.get('/jobs/hot', {}),
        apiClient.get('/dials/top-companies')
      ]);
      setState({ loading: false, jobs: _.slice(_.shuffle(jobs), 0, 6), companies });
    } catch (err) {
      setState({ loading: false, jobs: [], companies: [] });
      Sentry.captureException(err);
    }
  };

  useEffect(() => {
    loadData();
  }, [i18n.language]);

  const handleSignupClick = () => {
    googleAnalytics.event('homepage_user_signup');
    navigate(routes.signup);
  };

  const handleBoSignupClick = () => {
    googleAnalytics.event('homepage_company_signup');
    window.location.href = createBackofficeLink(backoffice.signup);
  };

  const handleAllJobsClick = (event) => {
    event.preventDefault();
    googleAnalytics.event('homepage_all_jobs');
    navigate(ALL_JOBS_LINK);
  };

  const handleCategoryClick = category => (event) => {
    const newTab = event.ctrlKey || event.metaKey;
    if (!newTab) {
      event.preventDefault();
      event.stopPropagation();
      category.cb();
    }
    googleAnalytics.event('homepage_category_click', { route: category.route });
  };

  const handleBlogPostClick = url => () => {
    googleAnalytics.event('homepage_blog_click', { url });
    window.location.href = url;
  };

  return (
    <div className={styles.intro}>
      <div className="container pb32 pt32">
        <TitleWithButton variant="h2" title={t('intro.categories')} className={styles.categoriesTitle}>
          <Link to={ALL_JOBS_LINK} onClick={handleAllJobsClick}>{t('intro.allJobs')}</Link>
        </TitleWithButton>
        <div className={cn('row', styles.topCategories)}>
          {categories.map(category => (
            <div key={category.id} className="col-xl-3 col-lg-4 col-md-4 col-sm-6 col-xs-12">
              <Card noBorder className={styles.category} onClick={handleCategoryClick(category)}>
                {category.la ? <i className={category.icon} /> : <Icon slug={category.icon} />}
                <div>
                  <h3>
                    <Link to={category.route} onClick={handleCategoryClick(category)}>
                      {t(`intro.category.${category.id}.title`)}
                    </Link>
                  </h3>
                  <p>
                    {t(`intro.category.${category.id}.description`)}
                  </p>
                </div>
              </Card>
            </div>
          ))}
        </div>
        <div className="text-center pt16">
          <Submit
            noMargin
            onClick={handleAllJobsClick}
            title={t('intro.gotoAllJobs')}
          />
        </div>
      </div>
      {!loggedIn && (
        <div className={styles.signupBlock}>
          <div className={styles.signupWrap}>
            <div className={styles.signup}>
              <h2>{t('intro.signupTitle')}</h2>
              <p>{t('intro.signupText')}</p>
              <div>
                <Submit
                  onClick={handleSignupClick}
                  className={styles.signupButton}
                  title={t('intro.signupCta')}
                />
              </div>
            </div>
          </div>
          <div className={cn(styles.signupWrap, styles.signupWrap2)}>
            <div className={cn(styles.signup, styles.signup2)}>
              <h2>{t('intro.boSignupTitle')}</h2>
              <p>{t('intro.boSignupText')}</p>
              <div>
                <Submit
                  onClick={handleBoSignupClick}
                  className={styles.signupButton}
                  title={t('intro.boSignupCta')}
                />
              </div>
            </div>
          </div>
        </div>
      )}
      <Loadable
        loading={state.loading}
        spinnerClassName={styles.introLoader}
        render={() => (
          <>
            {state.jobs.length > 0 && (
              <div className={cn('container pb32', { pt32: !loggedIn })}>
                <h2>{t('intro.topJobs')}</h2>
                <CardList>
                  {state.jobs.map(job => (
                    <Card key={job.slug} noBorder spacingBottom="small" className={styles.topJob}>
                      <JobPreview
                        noScore
                        withSmallTags
                        loggedIn={loggedIn}
                        job={job}
                        currency={currency}
                        withSeoLinks={withSeoLinks}
                        searchItems={searchItems}
                        onRoleClick={onRoleClick}
                        onSkillClick={onSkillClick}
                        onEmploymentTypeClick={onEmploymentTypeClick}
                        onCompanySizeClick={onCompanySizeClick}
                        onCountryClick={onCountryClick}
                        onCityClick={onCityClick}
                        onBenefitClick={onBenefitClick}
                        onRateClick={onRateClick}
                      />
                    </Card>
                  ))}
                </CardList>
                <div className="text-center pt16">
                  <Submit
                    noMargin
                    onClick={handleAllJobsClick}
                    title={t('intro.gotoAllJobs')}
                  />
                </div>
              </div>
            )}
            {state.companies.length > 0 && (
              <div className={styles.companies}>
                <div className="container">
                  <h2>{t('intro.companies')}</h2>
                  <div className="row">
                    {state.companies.map(company => (
                      <div
                        key={company.slug}
                        className={cn('col-xl-2 col-lg-2 col-md-4 col-sm-4 col-6', styles.company)}
                      >
                        <Link to={createRoute(routes.company.detail, { slug: company.slug })}>
                          <img src={createStaticsLink(company.logo)} alt="" />
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      />
      <div className="container pt32 pb16">
        <h2>{t('intro.blog')}</h2>
        <div className={cn('row', styles.blog)}>
          {_.range(1, 4).map(id => (
            <div
              key={id}
              className="col-xl-4 col-lg-4 col-md-12 mb16"
              onClick={handleBlogPostClick(t(`intro.posts.${id}.link`))}
            >
              <Card noBorder className={styles.post}>
                <h3>
                  <a href={t(`intro.posts.${id}.link`)}>{t(`intro.posts.${id}.title`)}</a>
                </h3>
                <img
                  src={t(`intro.posts.${id}.image`)}
                  loading="lazy"
                  srcSet={t(`intro.posts.${id}.srcset`)}
                  sizes={t(`intro.posts.${id}.sizes`)}
                  alt=""
                />
                <p>
                  {t(`intro.posts.${id}.perex`)}
                </p>
              </Card>
            </div>
          ))}
        </div>
      </div>
      <div className={styles.whyBox}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2>{t('page.home.whyJoin')}</h2>
            </div>
          </div>
          <div className="row">
            <div className="col-xl-3 col-lg-3 col-md-6 col-sm-12 mb16">
              <div className={styles.why}>
                <div>
                  <img src="/images/relevancy.jpg" alt="" />
                  <h3>
                    {t('page.about.po1-title')}
                  </h3>
                </div>
                <p>
                  <Trans i18nKey="userportal:page.about.po1-text">
                    <Fragment />
                  </Trans>
                </p>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-sm-12 mb16">
              <div className={styles.why}>
                <div>
                  <img src="/images/privacy.png" alt="" />
                  <h3>
                    {t('page.about.po2-title')}
                  </h3>
                </div>
                <p>
                  <Trans i18nKey="userportal:page.about.po2-text">
                    <Fragment />
                    <Fragment />
                  </Trans>
                </p>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-sm-12 mb16">
              <div className={styles.why}>
                <div>
                  <img src="/images/money.jpg" alt="" />
                  <h3>
                    {t('page.about.po3-title')}
                  </h3>
                </div>
                <p>{t('page.about.po3-text')}</p>
              </div>
            </div>
            <div className="col-xl-3 col-lg-3 col-md-6 col-sm-12 mb16">
              <div className={styles.why}>
                <div>
                  <img src="/images/usability.jpg" alt="" />
                  <h3>
                    {t('page.about.po4-title')}
                  </h3>
                </div>
                <p>
                  <Trans i18nKey="userportal:page.about.po4-text">
                    <Fragment />
                    <Fragment />
                  </Trans>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

HomeIntro.propTypes = {
  loggedIn: PropTypes.bool,
  currency: PropTypes.string,
  withSeoLinks: PropTypes.bool,
  searchItems: PropTypes.arrayOf(PropTypes.shape(itemPropTypes)),
  onFieldClick: PropTypes.func,
  onRoleClick: PropTypes.func,
  onSkillClick: PropTypes.func,
  onEmploymentTypeClick: PropTypes.func,
  onCompanySizeClick: PropTypes.func,
  onCountryClick: PropTypes.func,
  onCityClick: PropTypes.func,
  onBenefitClick: PropTypes.func,
  onRateClick: PropTypes.func,
  onSuitableForClick: PropTypes.func,
};

HomeIntro.defaultProps = {
  loggedIn: false,
  currency: DEFAULT_CURRENCY,
  withSeoLinks: false,
  searchItems: [],
  onFieldClick() {},
  onRoleClick() {},
  onSkillClick() {},
  onEmploymentTypeClick() {},
  onCompanySizeClick() {},
  onCountryClick() {},
  onCityClick() {},
  onBenefitClick() {},
  onRateClick() {},
  onSuitableForClick() {},
};

export default HomeIntro;
